import { useId, useLayoutEffect, useRef, useState, type ComponentProps, type FormEvent } from "react";
import { ListRow } from "@core/components/List";
import { Sheet } from "@core/components/Sheet";

/** The Ausgestaltung of a resource or weakness: a short note shown in the list and a long description read in its own sheet. */
export interface Elaboration {
  note?: string | null;
  details?: string | null;
}

type RowProps = ComponentProps<typeof ListRow>;

/**
 * A list row with an Ausgestaltung. The note stands under the name (or above the content below the row when the row has a subtitle of its
 * own); a long description shows as „mehr …“ and opens for reading. Both are edited only in the dialog behind the pen, or behind
 * „Ausgestaltung hinzufügen“ while there is neither.
 */
export function ElaboratedRow(props: RowProps & { subject: string; elaboration: Elaboration; onSave: (next: { note: string; details: string }) => Promise<boolean> }) {
  const { subject, elaboration, onSave, ...row } = props;
  const [mode, setMode] = useState<"read" | "edit" | null>(null);
  const has = !!(elaboration.note || elaboration.details);
  const summary = has ? (
    <span className="elaboration-summary">
      {elaboration.note && <span className="elaboration-note">{elaboration.note}</span>}
      {elaboration.details && (
        <button type="button" className="link elaboration-more" aria-label={`mehr … Beschreibung: ${subject}`} onClick={() => setMode("read")}>
          mehr …
        </button>
      )}
    </span>
  ) : (
    <button type="button" className="link small elaboration-add" aria-label={`Ausgestaltung hinzufügen: ${subject}`} onClick={() => setMode("edit")}>
      Ausgestaltung hinzufügen
    </button>
  );
  return (
    <ListRow
      {...row}
      subtitle={row.subtitle ?? summary}
      below={
        row.subtitle ? (
          <>
            {summary}
            {row.below}
          </>
        ) : (
          row.below
        )
      }
      actions={
        <>
          {row.actions}
          {has && (
            <button type="button" className="icon-button" aria-label={`Ausgestaltung bearbeiten: ${subject}`} title="Ausgestaltung bearbeiten" onClick={() => setMode("edit")}>
              ✎
            </button>
          )}
          {mode === "read" && <ElaborationReader subject={subject} elaboration={elaboration} onEdit={() => setMode("edit")} onClose={() => setMode(null)} />}
          {mode === "edit" && <ElaborationEditor subject={subject} elaboration={elaboration} onSave={onSave} onClose={() => setMode(null)} />}
        </>
      }
    />
  );
}

/** Reading the long description, with a way to edit it. */
function ElaborationReader(props: { subject: string; elaboration: Elaboration; onEdit: () => void; onClose: () => void }) {
  return (
    <Sheet
      title={props.subject}
      onClose={props.onClose}
      footer={
        <button type="button" className="button secondary" onClick={props.onEdit}>
          Bearbeiten
        </button>
      }
    >
      <div className="stack small-gap">
        {props.elaboration.note && <p className="muted">{props.elaboration.note}</p>}
        <div className="elaboration-text">{props.elaboration.details}</div>
      </div>
    </Sheet>
  );
}

/** Editing note and long description; „Übernehmen“ saves both, cancelling and Escape discard. */
function ElaborationEditor(props: { subject: string; elaboration: Elaboration; onSave: (next: { note: string; details: string }) => Promise<boolean>; onClose: () => void }) {
  const formId = useId();
  const [opener] = useState(() => document.activeElement as HTMLElement | null);
  const [note, setNote] = useState(props.elaboration.note ?? "");
  const [details, setDetails] = useState(props.elaboration.details ?? "");
  const [busy, setBusy] = useState(false);
  const area = useRef<HTMLTextAreaElement>(null);

  useLayoutEffect(() => {
    const element = area.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight + 2}px`;
  }, [details]);

  const close = () => {
    props.onClose();
    opener?.focus();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (note.trim() === (props.elaboration.note ?? "") && details.trim() === (props.elaboration.details ?? "")) {
      close();
      return;
    }
    setBusy(true);
    if (await props.onSave({ note: note.trim(), details: details.trim() })) close();
    else setBusy(false);
  };

  return (
    <Sheet
      title={`Ausgestaltung: ${props.subject}`}
      onClose={close}
      full
      footer={
        <>
          <button type="button" className="button secondary" onClick={close}>
            Abbrechen
          </button>
          <button type="submit" form={formId} className="button primary" disabled={busy}>
            Übernehmen
          </button>
        </>
      }
    >
      <form id={formId} className="stack" noValidate onSubmit={(e) => void submit(e)}>
        <label className="field">
          <span>Kurzer Hinweis</span>
          <input autoFocus value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
        <label className="field">
          <span>Ausführliche Beschreibung</span>
          <textarea ref={area} className="elaboration-details" rows={8} value={details} onChange={(e) => setDetails(e.target.value)} />
        </label>
      </form>
    </Sheet>
  );
}
